import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, Loader2, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { publicPreviewInit, contentTypeFor } from '../services/api';

// DropAFitsWidget — the anonymous "drop a FITS, get a preview" primitive.
// Used both as the hero on the Home page and standalone at /try. Handles
// the entire lifecycle: extension + size validation, POST /public/preview/init,
// PUT to S3 with progress, localStorage handoff, navigate to /preview/<id>.
// No auth, no options — one drop zone, one CTA, one outcome.

const ACCEPTED = ['.fit', '.fits', '.fz'];
const MAX_BYTES = 200 * 1024 * 1024;

type Phase = 'idle' | 'presigning' | 'uploading' | 'complete' | 'error';

type Props = {
  // Compact renders a shorter zone suitable for embedding inside a hero
  // section. Default (full) is a large standalone drop target.
  compact?: boolean;
};

const DropAFitsWidget: React.FC<Props> = ({ compact = false }) => {
  const nav = useNavigate();
  const [file, setFile] = React.useState<File | null>(null);
  const [phase, setPhase] = React.useState<Phase>('idle');
  const [pct, setPct] = React.useState(0);
  const [err, setErr] = React.useState<string | null>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const acceptedName = (n: string): boolean => {
    const l = n.toLowerCase();
    return ACCEPTED.some((e) => l.endsWith(e));
  };

  const submit = React.useCallback(async (f: File) => {
    setErr(null);
    if (!acceptedName(f.name)) {
      setErr(`Only ${ACCEPTED.join(' / ')} files — pick a raw FITS from your capture folder.`);
      return;
    }
    if (f.size > MAX_BYTES) {
      setErr(`File too large — max ${(MAX_BYTES / 1024 / 1024).toFixed(0)} MB per preview. Sign up for the full stacker.`);
      return;
    }
    setFile(f);
    setPhase('presigning');
    setPct(0);
    try {
      const init = await publicPreviewInit({
        filename: f.name,
        sizeBytes: f.size,
        contentType: contentTypeFor(f.name),
      });
      setPhase('uploading');
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', init.uploadUrl);
        xhr.setRequestHeader('Content-Type', contentTypeFor(f.name));
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setPct(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`S3 rejected upload: HTTP ${xhr.status} ${xhr.statusText}`));
        };
        xhr.onerror = () => reject(new Error('Network error uploading to S3'));
        xhr.send(f);
      });
      try {
        window.localStorage.setItem(
          `public-preview:${init.shareId}`,
          JSON.stringify({
            previewUrl: init.previewUrl,
            thumbUrl: init.thumbUrl,
            filename: f.name,
          }),
        );
      } catch { /* localStorage full or blocked — non-fatal */ }
      setPhase('complete');
      nav(`/preview/${init.shareId}`);
    } catch (e) {
      setErr((e as Error).message);
      setPhase('error');
    }
  }, [nav]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (phase === 'presigning' || phase === 'uploading') return;
    const f = e.dataTransfer.files?.[0];
    if (f) submit(f);
  };

  const busy = phase === 'presigning' || phase === 'uploading';
  const pad = compact ? 'p-8' : 'p-16';

  return (
    <div className="w-full">
      <div
        onDrop={onDrop}
        onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => !busy && inputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-3xl ${pad} text-center cursor-pointer transition-all backdrop-blur-md ${
          dragOver
            ? 'border-nebula-400 bg-nebula-500/15'
            : 'border-white/25 hover:border-white/45 bg-space-950/40 hover:bg-space-950/50'
        } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(',')}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) submit(f);
            e.target.value = '';
          }}
          className="hidden"
        />
        {phase === 'idle' && (
          <>
            <Upload className={`${compact ? 'h-10 w-10' : 'h-14 w-14'} mx-auto text-white/60 mb-3`} />
            <div className={`font-display font-semibold ${compact ? 'text-lg' : 'text-xl'} mb-1`}>
              Drop a FITS to see what we do
            </div>
            <div className="text-sm text-white/60">
              free · no signup · .fit / .fits / .fz up to {(MAX_BYTES / 1024 / 1024).toFixed(0)} MB
            </div>
          </>
        )}
        {phase === 'presigning' && (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-nebula-300 animate-spin mb-3" />
            <div className="text-sm text-white/70">Preparing upload…</div>
          </>
        )}
        {phase === 'uploading' && (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-nebula-300 animate-spin mb-3" />
            <div className="text-sm text-white/70 mb-3 truncate">
              Uploading {file?.name} · {pct}%
            </div>
            <div className="h-1 rounded-full bg-white/10 overflow-hidden max-w-md mx-auto">
              <motion.div
                className="h-full bg-nebula-400"
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.1 }}
              />
            </div>
          </>
        )}
        {phase === 'complete' && (
          <>
            <ImageIcon className="h-10 w-10 mx-auto text-emerald-300 mb-3" />
            <div className="text-sm text-white/70">Uploaded. Rendering preview…</div>
          </>
        )}
      </div>

      {err && (
        <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-200">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>{err}</div>
        </div>
      )}
    </div>
  );
};

export default DropAFitsWidget;
